import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, RotateCcw, Pencil, AlertTriangle } from 'lucide-react';
import { apiErrorMessage } from '../../lib/apiError';
import { findCatalogEntry, formatCer, isValidCerCode } from '../../lib/cerCatalog';
import Modal from '../ui/Modal';
import CerCombobox from './CerCombobox';
import { useAuthStore } from '../../store/authStore';
import { canWriteSettings } from '../../lib/constants';

/**
 * Sezione CRUD generica per le settings Rifiuti & CER (N1+N3): layout, validazione
 * e messaggi UNIFORMI tra i tipi. La tabella è in sola lettura; "Aggiungi" e
 * "Modifica" aprono il Modal generico (campi a larghezza piena, errori per-campo).
 * Il campo kind='cer' è un combobox ricercabile sul catalogo CER ufficiale
 * (lib/cerCatalog): la dicitura ufficiale si deriva a runtime dal codice e NON
 * si salva a DB; `nome_descrittivo` resta la descrizione nostra.
 */
const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-3 py-2 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const labelCls = 'block text-xs font-medium text-ink-soft mb-1';

export interface FieldOption { value: string; label: string }

export interface FieldDef {
  key: string;
  label: string;
  kind: 'text' | 'select' | 'cer';
  required?: boolean;
  immutable?: boolean;                 // non editabile in modifica
  /** Campo in sola lettura per QUESTO utente (gate di ruolo field-level del
   *  backend): input disabilitato e `required` non applicato. Diverso da
   *  `immutable`, che vale per tutti e solo in modifica. Il payload lo decide il
   *  consumer in onCreate/onUpdate: qui non si sa cosa vada omesso. */
  readOnly?: boolean;
  options?: FieldOption[];             // select statico
  optionsKey?: string;                 // select dinamico, da `dynamicOptions[optionsKey]`
  placeholder?: string;
  display?: (v: string | null | undefined) => string;  // resa in tabella
  /** Larghezza colonna in tabella (classe Tailwind per <col>): un
   *  SUGGERIMENTO, non una misura. La tabella è a layout automatico, quindi il
   *  browser parte da qui e poi stringe o allarga in base al contenuto e allo
   *  spazio: nessuna colonna può restare a zero. Era una misura vincolante
   *  finché la tabella era `table-fixed`, ed è così che DESCRIZIONE è finita a
   *  0px — vedi il commento sul <table>. Default: w-28 per kind 'cer'. */
  col?: string;
}

export interface CrudRow {
  id?: string;
  attivo?: boolean;
}

// Accesso dinamico a un campo (i tipi entità non hanno index signature).
function fieldVal(row: object, key: string): string | null {
  const v = (row as Record<string, unknown>)[key];
  return v == null ? null : String(v);
}

interface Props<T extends CrudRow> {
  description: string;
  fields: FieldDef[];
  rows: T[];
  loading: boolean;
  dynamicOptions?: Record<string, FieldOption[]>;
  /** Crea: riceve i valori dei campi (stringa; '' = vuoto). */
  onCreate: (values: Record<string, string>) => Promise<void>;
  /** Modifica (esclusi gli immutable). */
  onUpdate: (id: string, values: Record<string, string>) => Promise<void>;
  onToggle: (row: T) => Promise<void>;
  reload: () => void;
  isIncomplete?: (row: T) => boolean;
  statoLabels?: { attivo: string; disattivato: string };
  emptyMsg?: string;
  banner?: React.ReactNode;
  testidPrefix?: string;
  /** Titolo entità nel modal (es. "voce CER d'ingresso"). */
  entityLabel?: string;
}

type ModalState<T> = { mode: 'create' } | { mode: 'edit'; row: T } | null;

export default function SettingsCrudSection<T extends CrudRow>({
  description, fields, rows, loading, dynamicOptions = {},
  onCreate, onUpdate, onToggle, reload, isIncomplete,
  statoLabels, emptyMsg, banner, testidPrefix = 'crud',
  entityLabel,
}: Props<T>) {
  const { t } = useTranslation();
  // Scrittura Settings = {admin, DIR, RT}; AMM (e altri) sola lettura → niente
  // bottoni Aggiungi/Modifica/Disattiva né colonna azioni.
  const canWrite = canWriteSettings(useAuthStore((s) => s.user?.role));
  const effStatoLabels = statoLabels ?? { attivo: t('settings.crud.stato_attivo'), disattivato: t('settings.crud.stato_disattivato') };
  const effEmptyMsg = emptyMsg ?? t('settings.crud.vuoto');
  const effEntityLabel = entityLabel ?? t('settings.crud.entita');
  const [tableError, setTableError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState<T>>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const optionsFor = useCallback((f: FieldDef): FieldOption[] => {
    if (f.options) return f.options;
    if (f.optionsKey) return dynamicOptions[f.optionsKey] ?? [];
    return [];
  }, [dynamicOptions]);

  const hasField = useCallback(
    (key: string) => fields.some((f) => f.key === key),
    [fields],
  );

  function setVal(key: string, v: string) {
    setValues((s) => ({ ...s, [key]: v }));
    setFieldErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  }

  function openCreate() {
    setValues({});
    setFieldErrors({});
    setFormError(null);
    setModal({ mode: 'create' });
  }

  function openEdit(row: T) {
    const v: Record<string, string> = {};
    for (const f of fields) v[f.key] = fieldVal(row, f.key) ?? '';
    setValues(v);
    setFieldErrors({});
    setFormError(null);
    setModal({ mode: 'edit', row });
  }

  // Riferimento stabile: passata al Modal come onClose (una closure nuova a
  // ogni render farebbe ri-registrare il listener ESC a ogni keystroke).
  // Il guard "non chiudere durante il salvataggio" legge un ref per non
  // dipendere dallo stato `saving`.
  const savingRef = useRef(false);
  const closeModal = useCallback(() => {
    if (!savingRef.current) setModal(null);
  }, []);

  /** Selezione a catalogo: classificazione precompilata da `hazardous` (sempre
   *  modificabile — esistono i codici a specchio); la dicitura ufficiale viene
   *  proposta come nome_descrittivo SOLO se il campo è vuoto. */
  function handleCatalogSelect(entry: { code: string; description: string; hazardous: boolean }) {
    setValues((s) => {
      const next = { ...s };
      if (hasField('classificazione')) {
        next['classificazione'] = entry.hazardous ? 'pericoloso' : 'non_pericoloso';
      }
      if (hasField('nome_descrittivo') && !(s['nome_descrittivo'] ?? '').trim()) {
        next['nome_descrittivo'] = entry.description;
      }
      return next;
    });
    setFieldErrors((e) => {
      const next = { ...e };
      delete next['cer'];
      return next;
    });
  }

  function validate(editing: boolean): boolean {
    const errs: Record<string, string> = {};
    for (const f of fields) {
      if ((editing && f.immutable) || f.readOnly) continue;
      const v = (values[f.key] ?? '').trim();
      if (f.required && !v) {
        errs[f.key] = t('settings.crud.val_obbligatorio', { campo: f.label });
        continue;
      }
      if (f.kind === 'cer' && v && !isValidCerCode(v)) {
        errs[f.key] = t('settings.crud.val_cer');
      }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!modal) return;
    const editing = modal.mode === 'edit';
    if (!validate(editing)) return;
    setSaving(true);
    savingRef.current = true;
    setFormError(null);
    try {
      if (modal.mode === 'create') {
        await onCreate(values);
      } else if (modal.row.id) {
        await onUpdate(modal.row.id, values);
      }
      setModal(null);
      reload();
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, t('settings.crud.errore_salvataggio'));
      // 422 del vincolo (cer, nome_descrittivo) → errore sui due campi coinvolti.
      if (/esiste già/i.test(msg) && hasField('cer')) {
        setFieldErrors({ cer: msg, nome_descrittivo: t('settings.crud.cambia_codice') });
      } else {
        setFormError(msg);
      }
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  }

  async function handleToggle(row: T) {
    setTableError(null);
    try {
      await onToggle(row);
      reload();
    } catch (err: unknown) {
      setTableError(apiErrorMessage(err, t('settings.crud.errore_aggiornamento')));
    }
  }

  function renderInput(f: FieldDef, editing: boolean) {
    const value = values[f.key] ?? '';
    const testid = `${testidPrefix}-field-${f.key}`;
    if (editing && f.immutable) {
      return <div className={`${inputCls} bg-stone-bg text-ink-soft`}>{value || '—'}</div>;
    }
    if (f.kind === 'cer') {
      const entry = findCatalogEntry(value);
      const completo = isValidCerCode(value.trim());
      return (
        <div className="space-y-1">
          <CerCombobox
            value={value}
            onChange={(v) => setVal(f.key, v)}
            onSelect={handleCatalogSelect}
            placeholder={f.placeholder}
            testid={testid}
          />
          {entry && (
            <p className="text-xs text-ink-soft" data-testid={`${testidPrefix}-dicitura-ufficiale`}>
              <span className="font-medium">{entry.hazardous ? t('settings.crud.dicitura_pericoloso') : t('settings.crud.dicitura')}</span>{' '}
              {entry.description}
            </p>
          )}
          {!entry && completo && (
            <p className="text-xs text-amber-700 inline-flex items-center gap-1"
              data-testid={`${testidPrefix}-fuori-catalogo`}>
              <AlertTriangle size={12} /> {t('settings.crud.fuori_catalogo')}
            </p>
          )}
        </div>
      );
    }
    if (f.kind === 'select') {
      return (
        <select className={`${inputCls} cursor-pointer`} value={value} disabled={f.readOnly}
          onChange={(e) => setVal(f.key, e.target.value)} data-testid={testid}>
          <option value="">—</option>
          {optionsFor(f).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input type="text" className={inputCls} placeholder={f.placeholder} value={value}
        disabled={f.readOnly}
        onChange={(e) => setVal(f.key, e.target.value)} data-testid={testid} />
    );
  }

  function renderCerCell(row: T, f: FieldDef) {
    const raw = fieldVal(row, f.key) ?? '';
    const entry = findCatalogEntry(raw);
    // Asterisco dei pericolosi DERIVATO da classificazione (post-V50 il DB ha
    // sole cifre); helper unico formatCer, lo stesso degli altri punti di stampa.
    const display = formatCer(raw, fieldVal(row, 'classificazione'));
    return (
      <td key={f.key} className="py-2.5 pr-3 align-top">
        <span className="font-mono text-ink-dark whitespace-nowrap">{display || '—'}</span>
        {/* Il sottotesto e' `line-clamp-2`, NON `truncate`: con il layout
            automatico una riga `nowrap` pretende per se' tutta la larghezza del
            suo testo, e una dicitura EUR-Lex lunga (19 12 12) si porterebbe via
            mezza tabella. Andando a capo e tagliata a due righe, la colonna
            resta stretta e la riga non cresce; il testo intero sta nel `title`. */}
        {entry ? (
          <p className="text-[11px] text-ink-faint leading-tight line-clamp-2" title={entry.description}>
            {entry.description}
          </p>
        ) : (
          <p className="leading-tight">
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap"
              data-testid={`${testidPrefix}-badge-fuori-catalogo-${row.id}`}>
              <AlertTriangle size={10} /> {t('settings.crud.non_a_catalogo')}
            </span>
          </p>
        )}
      </td>
    );
  }

  const modalEditing = modal?.mode === 'edit';

  return (
    <div className="space-y-5 bg-white border border-stone-mid rounded-xl p-5">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-ink-mid">{description}</p>
        {canWrite && (
          <button type="button" onClick={openCreate}
            data-testid={`${testidPrefix}-add-btn`}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors flex items-center gap-1.5 whitespace-nowrap flex-shrink-0">
            <Plus size={15} /> {t('settings.crud.aggiungi')}
          </button>
        )}
      </div>

      {banner}
      {tableError && <div className="p-3 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid={`${testidPrefix}-error`}>{tableError}</div>}

      {loading ? (
        <div className="text-center py-6 text-sm text-ink-soft">{t('settings.crud.caricamento')}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-sm text-ink-soft">{effEmptyMsg}</div>
      ) : (
        <table className="w-full text-sm">
          {/* NIENTE `table-fixed`. Con quello la larghezza delle colonne è
              decisa dai <col>, e la colonna SENZA larghezza — la descrizione —
              prende quel che avanza: se le altre hanno già consumato tutto,
            avanza ZERO. È esattamente quello che succedeva qui, e a ogni
              dimensione di finestra, perché lo spazio di questa tabella non
              dipende dalla finestra: la pagina Settings è `max-w-6xl` e ha una
              sua nav da `w-56` più `gap-6`, quindi alla tabella restano ~816px
              allargando lo schermo quanto si vuole (774 su un 1366 con la
              sidebar). Le sei colonne dei CER rifiuti prodotti ne chiedevano
              832: DESCRIZIONE a 0px, la sua intestazione — una parola sola, che
              non va a capo — sopra TIPOLOGIA, e le tre righe 19 12 03 (rame,
              alluminio, silicio) indistinguibili. Il layout AUTOMATICO non ha
              questo modo di fallire: distribuisce per contenuto e nessuna
              colonna scende sotto il suo minimo. Misurato in browser: 814px di
              tabella su 816 disponibili, zero debordi. */}
          <colgroup>
            {fields.map((f) => (
              <col key={f.key} className={f.col ?? (f.kind === 'cer' ? 'w-28' : undefined)} />
            ))}
            <col className="w-28" />
            {canWrite && <col className="w-28" />}
          </colgroup>
          <thead>
            <tr className="border-b border-stone-mid text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              {/* NIENTE `whitespace-nowrap` sulle intestazioni dei campi: col
                  layout automatico una intestazione `nowrap` diventa il minimo
                  della colonna e se la porta via tutta. Lasciandole andare a
                  capo il caso peggiore e' un'intestazione su due righe, che si
                  legge. Stato e Azioni restano `nowrap` apposta: sono due
                  parole corte, e li' il `nowrap` difende l'allineamento dei
                  bottoni. */}
              {fields.map((f) => <th key={f.key} className="py-2 pr-3">{f.label}</th>)}
              <th className="py-2 pr-3 whitespace-nowrap">{t('settings.crud.col_stato')}</th>
              {canWrite && <th className="py-2 text-right whitespace-nowrap">{t('settings.crud.col_azioni')}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const incompleto = isIncomplete?.(row) ?? false;
              return (
                <tr key={row.id ?? fieldVal(row, fields[0]!.key) ?? ''} className="border-b border-stone-mid/60" data-testid={`${testidPrefix}-row-${row.id}`}>
                  {fields.map((f) => {
                    if (f.kind === 'cer') return renderCerCell(row, f);
                    const raw = fieldVal(row, f.key);
                    const text = f.display ? f.display(raw) : (raw ?? '—');
                    return (
                      <td key={f.key} className="py-2.5 pr-3 text-ink-mid align-top">
                        {/* Il testo va a capo, non si tronca: `truncate` e'
                            `nowrap`, e col layout automatico avrebbe preteso
                            l'intera larghezza del valore piu' lungo. Il
                            `title` resta per chi passa sopra. */}
                        <div title={typeof text === 'string' ? text : undefined}>{text}</div>
                      </td>
                    );
                  })}
                  <td className="py-2.5 pr-3 align-top">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${row.attivo ? 'bg-green-100 text-green-700' : 'bg-stone-200 text-ink-soft'}`}>
                        {row.attivo ? effStatoLabels.attivo : effStatoLabels.disattivato}
                      </span>
                      {incompleto && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-700 whitespace-nowrap leading-tight" title={t('settings.crud.attributi_mancanti')}>
                          <AlertTriangle size={12} /> {t('settings.crud.da_completare')}
                        </span>
                      )}
                    </div>
                  </td>
                  {canWrite && (
                    <td className="py-2.5 align-top">
                      {/* Azioni impilate: la riga è già su due linee (codice+dicitura),
                          così la colonna resta stretta senza far crescere la riga. */}
                      <div className="flex flex-col items-end gap-1">
                        <button type="button" onClick={() => openEdit(row)}
                          data-testid={`${testidPrefix}-edit-btn-${row.id}`}
                          className="text-ink-soft hover:text-ink-dark inline-flex items-center gap-1 text-xs whitespace-nowrap"><Pencil size={13} /> {t('settings.crud.modifica')}</button>
                        <button type="button" onClick={() => handleToggle(row)}
                          className="text-ink-soft hover:text-ink-dark inline-flex items-center gap-1 text-xs whitespace-nowrap">
                          {row.attivo ? <><Trash2 size={13} /> {t('settings.crud.disattiva')}</> : <><RotateCcw size={13} /> {t('settings.crud.riattiva')}</>}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal && (
        <Modal
          title={modal.mode === 'create' ? t('settings.crud.modal_aggiungi', { entita: effEntityLabel }) : t('settings.crud.modal_modifica', { entita: effEntityLabel })}
          onClose={closeModal}
          testid={`${testidPrefix}-modal`}
          footer={
            <>
              <button type="button" onClick={closeModal} disabled={saving}
                className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
                {t('common.azioni.annulla')}
              </button>
              <button type="button" onClick={handleSave} disabled={saving}
                data-testid={`${testidPrefix}-modal-save`}
                className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
                {saving ? t('settings.crud.salvataggio') : t('settings.crud.salva')}
              </button>
            </>
          }
        >
          {fields.map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}{f.required ? ' *' : ''}</label>
              {renderInput(f, modalEditing)}
              {fieldErrors[f.key] && (
                <p className="text-xs text-accent-danger mt-1" data-testid={`${testidPrefix}-error-${f.key}`}>
                  {fieldErrors[f.key]}
                </p>
              )}
            </div>
          ))}
          {formError && (
            <div className="p-3 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid={`${testidPrefix}-modal-error`}>
              {formError}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
